// ─── Bézier Pen Tool – Math & Path Utilities ─────────────────────────────────
import type { BezierAnchor, BezierHandle } from '../types/beizure.types';

/** Mirror a handle around an anchor (smooth constraint) */
export function mirrorHandle(
  anchor: { x: number; y: number },
  draggedHandle: BezierHandle
): BezierHandle {
  return {
    x: 2 * anchor.x - draggedHandle.x,
    y: 2 * anchor.y - draggedHandle.y,
  };
}

/**
 * Build an SVG path `d` string from a list of BézierAnchors.
 * Uses cubic bézier segments: the outHandle of anchor[i] and
 * the inHandle of anchor[i+1] define the curve.
 */
export function buildSvgPath(anchors: BezierAnchor[], closed: boolean): string {
  if (anchors.length < 1) return '';

  const first = anchors[0];
  let d = `M ${first.x} ${first.y}`;

  for (let i = 1; i < anchors.length; i++) {
    const prev = anchors[i - 1];
    const curr = anchors[i];
    d += ` C ${prev.outHandle.x} ${prev.outHandle.y}, ${curr.inHandle.x} ${curr.inHandle.y}, ${curr.x} ${curr.y}`;
  }

  if (closed && anchors.length >= 2) {
    const last = anchors[anchors.length - 1];
    const fst = anchors[0];
    d += ` C ${last.outHandle.x} ${last.outHandle.y}, ${fst.inHandle.x} ${fst.inHandle.y}, ${fst.x} ${fst.y}`;
    d += ' Z';
  }

  return d;
}

/**
 * Build a flat Konva-compatible points array for a polyline fallback.
 * Not used for rendering but useful for bounding-box estimates.
 */
export function anchorsToFlatPoints(anchors: BezierAnchor[]): number[] {
  return anchors.flatMap(a => [a.x, a.y]);
}

/**
 * Compute a point on a cubic Bézier at parameter t.
 * P0 = start, P1 = cp1, P2 = cp2, P3 = end
 */
export function cubicBezierPoint(
  p0: BezierHandle, p1: BezierHandle, p2: BezierHandle, p3: BezierHandle,
  t: number
): BezierHandle {
  const mt = 1 - t;
  return {
    x: mt ** 3 * p0.x + 3 * mt ** 2 * t * p1.x + 3 * mt * t ** 2 * p2.x + t ** 3 * p3.x,
    y: mt ** 3 * p0.y + 3 * mt ** 2 * t * p1.y + 3 * mt * t ** 2 * p2.y + t ** 3 * p3.y,
  };
}

/**
 * Estimate the approximate length of a cubic Bézier segment by
 * sampling N points.
 */
export function cubicBezierLength(
  p0: BezierHandle, p1: BezierHandle, p2: BezierHandle, p3: BezierHandle,
  samples = 20
): number {
  let len = 0;
  let prev = cubicBezierPoint(p0, p1, p2, p3, 0);
  for (let i = 1; i <= samples; i++) {
    const curr = cubicBezierPoint(p0, p1, p2, p3, i / samples);
    const dx = curr.x - prev.x;
    const dy = curr.y - prev.y;
    len += Math.sqrt(dx * dx + dy * dy);
    prev = curr;
  }
  return len;
}

/**
 * Estimate total path length across all segments.
 */
export function bezierPathLength(anchors: BezierAnchor[], closed: boolean): number {
  if (anchors.length < 2) return 0;
  let total = 0;
  for (let i = 1; i < anchors.length; i++) {
    const prev = anchors[i - 1];
    const curr = anchors[i];
    total += cubicBezierLength(prev, prev.outHandle, curr.inHandle, curr);
  }
  if (closed && anchors.length >= 2) {
    const last = anchors[anchors.length - 1];
    const fst = anchors[0];
    total += cubicBezierLength(last, last.outHandle, fst.inHandle, fst);
  }
  return total;
}

/**
 * Hit-test a point against a cubic Bézier path.
 * Samples N points per segment and checks distance.
 */
export function isPointNearBezierPath(
  px: number, py: number,
  anchors: BezierAnchor[],
  closed: boolean,
  threshold: number,
  samples = 40
): boolean {
  if (anchors.length < 2) return false;

  const segments: [BezierAnchor, BezierAnchor][] = [];
  for (let i = 1; i < anchors.length; i++) {
    segments.push([anchors[i - 1], anchors[i]]);
  }
  if (closed && anchors.length >= 2) {
    segments.push([anchors[anchors.length - 1], anchors[0]]);
  }

  for (const [a, b] of segments) {
    for (let i = 0; i <= samples; i++) {
      const t = i / samples;
      const pt = cubicBezierPoint(a, a.outHandle, b.inHandle, b, t);
      const dx = pt.x - px;
      const dy = pt.y - py;
      if (dx * dx + dy * dy <= threshold * threshold) return true;
    }
  }
  return false;
}

/**
 * Axis-aligned bounding box of all anchor + handle points.
 * Fast approximation (not tight hull, but sufficient for most uses).
 */
export function bezierBoundingBox(anchors: BezierAnchor[]): { x: number; y: number; w: number; h: number } {
  if (anchors.length === 0) return { x: 0, y: 0, w: 0, h: 0 };

  let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
  for (const a of anchors) {
    for (const pt of [a, a.inHandle, a.outHandle]) {
      if (pt.x < minX) minX = pt.x;
      if (pt.y < minY) minY = pt.y;
      if (pt.x > maxX) maxX = pt.x;
      if (pt.y > maxY) maxY = pt.y;
    }
  }
  return { x: minX, y: minY, w: maxX - minX, h: maxY - minY };
}

/** Generate a unique anchor id */
export function newAnchorId(): string {
  return `anchor-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
}

/**
 * Create a default anchor with coincident handles (corner point).
 * Calling code should update handles after creation if needed.
 */
export function createAnchor(x: number, y: number, smooth = true): BezierAnchor {
  return {
    id: newAnchorId(),
    x,
    y,
    inHandle: { x, y },
    outHandle: { x, y },
    smooth,
  };
}